import { completeStage, selectStage } from '../../../redux/slices/stageSlice';
import {
	display,
	fetchPrivacyConsentData,
	selectSession,
	setGuestVerificationLazyLoad,
} from '../../../redux/slices/sessionSlice';
import React, { useEffect, useState } from 'react';
import {
	submitSignature,
	submitSignature11,
} from '../../../redux/slices/signatureSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

import BottomDrawer from '../../../components/BottomDrawer';
import { DarkButton } from '../../../components//dark-button';
import InfoCardItem1 from '../../../components/info-card-item1';
import Loading from '../../../components/loading';
import { selectProfile } from '../../../redux/slices/profileSlice';
import SignatureCanvas from '../../../components/SignatureCanvas';
import styles from './page.module.css';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';

type PrivacyConsentItem = {
	multiLanguageTextID: number;
	textName: string;
	textValue: string;
	groupName: string;
	additionalText?: string;
	languageCode: string;
};

export default function PrivacyStatement() {
	const { t, i18n } = useTranslation();
	const [signature, setSignature] = useState<string | null>(null);
	const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [showPrivacyDrawer, setShowPrivacyDrawer] = useState(false);
	const [signatureCanvasKey, setSignatureCanvasKey] = useState(0);
	const [privacyNoticeType, setPrivacyNoticeType] =
		useState<string>('detprivacy');
	const [privacyNoticeContent, setPrivacyNoticeContent] = useState<string>('');
	const [complete, setComplete] = useState(false);
	const [consentStates, setConsentStates] = useState<Record<string, boolean>>(
		{}
	);
	const [propertyConsentStates, setPropertyConsentStates] = useState<
		Record<string, boolean>
	>({});

	const dispatch = useAppDispatch();
	const {
		token,
		reservation,
		propertyInfo,
		privacyConsentData,
		propertyPrivacyConsentData,
	} = useAppSelector(selectSession);
	const { stages, stage } = useAppSelector(selectStage);
	const { currentProfile } = useAppSelector(selectProfile);

	const privacyCards = [
		{
			iconClass: 'privacy-icon-fullname',
			title: t('PrivacyStatementCardTitle1'),
		},
		{
			iconClass: 'privacy-icon-phone',
			title: t('PrivacyStatementCardTitle2'),
		},
		{
			iconClass: 'privacy-icon-biometricdata',
			title: t('PrivacyStatementCardTitle3'),
		},
		{
			iconClass: 'privacy-icon-document',
			title: t('PrivacyStatementCardTitle4'),
		},
	];
	useEffect(() => {
		if (currentProfile && currentProfile.isPrimary === false) {
			dispatch(display(true));
			setComplete(true);
		}
	}, [currentProfile?.isPrimary, dispatch]);

	useEffect(() => {
		if (token) {
			const languageCode = (i18n?.language || 'en').split('-')[0].toLowerCase();
			dispatch(setGuestVerificationLazyLoad(true));
			dispatch(
				fetchPrivacyConsentData({
					languageCode,
					groupName: 'DETPrivacy',
				})
			);
			dispatch(
				fetchPrivacyConsentData({
					languageCode,
					groupName: 'PropertyPrivacy',
				})
			);
		}
	}, [dispatch, i18n?.language, token]);

	useEffect(() => {
		if (!token) return;

		const shouldShowLoader =
			privacyConsentData === null || propertyPrivacyConsentData === null;

		dispatch(setGuestVerificationLazyLoad(shouldShowLoader));
	}, [dispatch, token, privacyConsentData, propertyPrivacyConsentData]);

	useEffect(() => {
		if (privacyConsentData?.length) {
			const initial: Record<string, boolean> = {};
			privacyConsentData.forEach((item: PrivacyConsentItem) => {
				const key = String(item?.multiLanguageTextID ?? item?.textName);
				initial[key] = false;
			});
			setConsentStates(initial);
		}
	}, [privacyConsentData]);

	useEffect(() => {
		if (!propertyPrivacyConsentData?.length) {
			setPropertyConsentStates({});
			return;
		}

		const initial: Record<string, boolean> = {};
		propertyPrivacyConsentData.forEach((item: PrivacyConsentItem) => {
			const key = String(item?.multiLanguageTextID ?? item?.textName);
			initial[key] = false;
		});
		setPropertyConsentStates(initial);
	}, [propertyPrivacyConsentData]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	useEffect(() => {
		if (complete) {
			dispatch(
				completeStage({
					token,
					stageID: stage?.stepID,
					ReservationID: reservation?.ReservationID,
					type: 'checkin',
					confirmationNo: reservation?.PmsConfirmationNo,
					stages,
					propertyCode: propertyInfo?.propertyCode ?? '',
					profileID: currentProfile?.profileID ?? null,
				})
			).finally(() => {
				dispatch(display(false));
			});
		}
	}, [
		complete,
		currentProfile?.profileID,
		dispatch,
		propertyInfo?.propertyCode,
		reservation?.PmsConfirmationNo,
		reservation?.ReservationID,
		stage?.stepID,
		stages,
		token,
	]);

	const consentItems: PrivacyConsentItem[] = privacyConsentData ?? [];
	const propertyConsentItems: PrivacyConsentItem[] =
		propertyPrivacyConsentData ?? [];

	const areAllConsentsChecked =
		consentItems.length === 0
			? true
			: consentItems.every((item: PrivacyConsentItem) => {
					const key = String(item?.multiLanguageTextID ?? item?.textName);
					return consentStates[key];
			  });

	const areAllPropertyConsentsChecked =
		propertyConsentItems.length === 0
			? true
			: propertyConsentItems.every((item: PrivacyConsentItem) => {
					const key = String(item?.multiLanguageTextID ?? item?.textName);
					return propertyConsentStates[key];
			  });

	const handleSignatureChange = (
		newSignature: string | null,
		isEmpty: boolean
	) => {
		setSignature(newSignature);
		setIsSignatureEmpty(isEmpty);
	};

	const handleClearSignature = () => {
		setSignatureCanvasKey((currentKey) => currentKey + 1);
		handleSignatureChange(null, true);
	};

	const handleProceed1 = async () => {
		if (!signature || isSignatureEmpty) return;

		setIsSubmitting(true);

		try {
			const result = await dispatch(
				submitSignature11({
					token: token!,
					reservationId: reservation?.ReservationID,
					confirmationNo: reservation?.PmsConfirmationNo,
					signature,
					propertyCode: propertyInfo?.propertyCode ?? '',
				})
			).unwrap();

			if (result?.result === true) {
				setComplete(true);
			} else {
				Swal.fire({
					title: 'Signature Submission Failed',
					text: 'Failed to submit signature. Please try again.',
					icon: 'error',
				});
			}
		} catch (error) {
			console.error('Signature submission failed:', error);
			Swal.fire({
				title: 'Signature Submission Failed',
				text: 'Failed to submit signature. Please try again.',
				icon: 'error',
			});
		} finally {
			setIsSubmitting(false);
		}
	};
	const base64ToBlob = (base64Data: string): Blob => {
		const parts = base64Data.split(',');

		const mimeType = parts[0]?.match(/data:(.*?);base64/)?.[1] || 'image/png';

		const binaryString = atob(parts[1]);

		const buffer = new ArrayBuffer(binaryString.length);
		const bytes = new Uint8Array(buffer);

		for (let i = 0; i < binaryString.length; i++) {
			bytes[i] = binaryString.charCodeAt(i);
		}

		return new Blob([buffer], {
			type: mimeType,
		});
	};

	const handleProceed = async () => {
		if (!signature || isSignatureEmpty) return;

		setIsSubmitting(true);

		try {
			const signatureBlob = base64ToBlob(signature);

			const result = await dispatch(
				submitSignature({
					token: token!,
					reservationId: reservation?.ReservationID,
					confirmationNo: reservation?.PmsConfirmationNo,
					signature: signatureBlob,
					propertyCode: propertyInfo?.propertyCode ?? '',
				})
			).unwrap();

			if (result?.result === true) {
				setComplete(true);
			} else {
				Swal.fire({
					title: 'Signature Submission Failed',
					text: 'Failed to submit signature. Please try again.',
					icon: 'error',
				});
			}
		} catch (error) {
			console.error('Signature submission failed:', error);

			Swal.fire({
				title: 'Signature Submission Failed',
				text: 'Failed to submit signature. Please try again.',
				icon: 'error',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const renderConsentText = (
		text: string,
		groupName: string,
		additionalText?: string
	) => {
		const noticeTriggers = ['privacy notice', 'terms & conditions'];
		const lower = text.toLowerCase();

		let matchIndex = -1;
		let matchTerm = '';

		for (const trigger of noticeTriggers) {
			const idx = lower.indexOf(trigger);
			if (idx >= 0 && (matchIndex === -1 || idx < matchIndex)) {
				matchIndex = idx;
				matchTerm = trigger;
			}
		}

		if (matchIndex === -1) return text;

		const before = text.slice(0, matchIndex);
		const match = text.slice(matchIndex, matchIndex + matchTerm.length);
		const after = text.slice(matchIndex + matchTerm.length);

		return (
			<>
				{before}
				<span
					className="privacy-notice-link fw-semibold"
					style={{ cursor: 'pointer' }}
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();
						setPrivacyNoticeType(String(groupName ?? '').toLowerCase());
						setPrivacyNoticeContent(additionalText ?? '');
						setShowPrivacyDrawer(true);
					}}>
					{match}
				</span>
				{after}
			</>
		);
	};

	if (currentProfile && currentProfile.isPrimary === false) {
		return null;
	}

	return (
		<>
			<div
				className="d-flex flex-column justify-content-between"
				style={{ overflowX: 'hidden' }}>
				<div className="container pt-4 pt-md-5 privacy-content">
					{/* <div className="app-logo" /> */}

					<div className="privacy-shell">
						{/* <div className="privacy-hero text-center">
							<h2 className="privacy-title">{t('PrivacyStatementTitle')}</h2>
							<p className="privacy-description">
								{t('PrivacyStatementDescription')}
							</p>
						</div> */}

						<div className="privacy-layout mt-4">
							<div className="text-center">
								<h2 className="privacy-title">{t('PrivacyStatementTitle')}</h2>
								<p className="privacy-description">
									{t('PrivacyStatementDescription')}
								</p>

								{/* <div className="privacy-input-wrapper mt-4 text-start">
									<label className="privacy-input-label">
										{t('PrivacyStatementEmailLabel')}:
									</label>
									<input
										type="email"
										className="privacy-input"
										value={currentProfile?.email || ''}
										placeholder={t('PrivacyStatementEmailPlaceholder')}
										readOnly
									/>
								</div> */}

								<div className="privacy-consent-list mt-4">
									{consentItems.map((item) => {
										const key = String(
											item?.multiLanguageTextID ?? item?.textName
										);

										return (
											<div className="privacy-consent-item" key={key}>
												<label className="privacy-checkbox-label">
													<input
														type="checkbox"
														className="privacy-checkbox"
														checked={!!consentStates[key]}
														disabled={isSubmitting} // ✅ ADD THIS
														onChange={(e) =>
															setConsentStates((prev) => ({
																...prev,
																[key]: e.target.checked,
															}))
														}
													/>
													<span className="privacy-consent-text">
														{renderConsentText(
															item?.textValue ?? '',
															item?.groupName ?? 'det',
															item?.additionalText
														)}
													</span>
												</label>
											</div>
										);
									})}
								</div>
							</div>

							<div className="privacy-panel privacy-signature-panel">
								<h5 className="privacy-signature-title">{t('Signature')}</h5>

								{propertyConsentItems.length > 0 && (
									<div className="privacy-consent-list mt-4 privacy-property-consents">
										{propertyConsentItems.map((item) => {
											const key = String(
												item?.multiLanguageTextID ?? item?.textName
											);

											return (
												<div className="" key={`property-${key}`}>
													<label className="privacy-checkbox-label">
														<input
															type="checkbox"
															className="privacy-checkbox"
															checked={!!propertyConsentStates[key]}
															disabled={isSubmitting} // ✅ ADD THIS
															onChange={(e) =>
																setPropertyConsentStates((prev) => ({
																	...prev,
																	[key]: e.target.checked,
																}))
															}
														/>
														<span className="privacy-consent-text">
															{renderConsentText(
																item?.textValue ?? '',
																item?.groupName ?? 'propertyprivacy',
																item?.additionalText
															)}
														</span>
													</label>
												</div>
											);
										})}
									</div>
								)}
								<strong className="d-block text-sm text-start mt-3 mb-1">
									{t('PleaseProvideSignatureBelow')}
								</strong>
								<div className="d-flex justify-content-center privacy-signature-canvas-wrap">
									<div className="privacy-signature-canvas">
										<SignatureCanvas
											key={signatureCanvasKey}
											onSignatureChange={handleSignatureChange}
											disabled={isSubmitting}
										/>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="fixed-bottom w-100 px-3 py-3 border-top theme-footer d-flex justify-content-center privacy-footer">
					<DarkButton
						text={t('PrivacyStatementButton')}
						onClick={handleProceed}
						filled
						hideArrow
						disabled={
							!areAllConsentsChecked ||
							!areAllPropertyConsentsChecked ||
							isSignatureEmpty ||
							isSubmitting
						}
						loader={isSubmitting}
						className="det-proceed-button"
					/>
				</div>
			</div>

			<BottomDrawer
				show={showPrivacyDrawer}
				onClose={() => setShowPrivacyDrawer(false)}
				title={
					(privacyNoticeType || '') === 'detprivacy' ||
					(privacyNoticeType || '') === 'det'
						? t('Dubai DET Privacy Notice')
						: t('Privacy Notice')
				}>
				<div
					className={`prose prose-sm max-w-none ${styles.privacyDrawerContent}`}>
					<div className="privacy-drawer-header mb-3">
						{(privacyNoticeType || '') === 'detprivacy' ||
						(privacyNoticeType || '') === 'det' ? (
							<>
								<div className="privacy-drawer-title">
									{t('Privacy Notice for Seamless Check-in')}
								</div>
								<div
									className="privacy-drawer-logo"
									aria-label="Dubai DET"
									role="img"
								/>
							</>
						) : (
							<div className="privacy-drawer-title">
								{t('Privacy Notice for Seamless Check-in Hotel')}
							</div>
						)}
					</div>
					{privacyNoticeContent ? (
						<div
							className="text-muted mb-0"
							dangerouslySetInnerHTML={{ __html: privacyNoticeContent }}
						/>
					) : (
						<p className="text-muted mb-0">&nbsp;</p>
					)}
				</div>
			</BottomDrawer>
		</>
	);
}
